import { useDispatch, useStore } from "react-redux";
import {
  AddNodeToJourneyPayload,
  NODE_TYPE,
  NodeMutationResponse,
  PasteNodePayload,
  PasteNodeResponse,
  RFNodeType,
  WorkflowNodeData,
} from "../components/GraphBuilder/Nodes/utils/types";
import { RootState } from "../../../redux/store";
import { useCallback } from "react";
import {
  createRFNode,
  getDefaultNodeData,
  insertNode,
} from "../util/graphOperations";
import { RULE_NODE_TYPES } from "../components/GraphBuilder/Nodes/utils/constants";
import { denormalizeGraph } from "../util/normalizedHelpers";
import {
  markNodesInvalid,
  removeNodeFromGraph,
  setCopiedNode,
  setNodeFromApiByPreviousId,
  setNodesAndEdges,
  setPendingPanTarget,
  setPlacementIntent,
} from "../../../redux/slices/dynamicTriggerSlice";
import {
  dynamicTriggerEngineApi,
  useAddNodeToJourneyMutation,
  usePasteNodeMutation,
} from "../../../redux/services/dynamicTriggerEngineApi";
import {
  FIELD_AFFECTING_NODE_TYPES,
  NODE_TYPE_TO_CATEGORY,
  TRIGGER_NODE_TYPES,
} from "../components/GraphBuilder/Nodes/utils/constants";
import { customToast } from "../../../utils/custom-toast/custom-toast";
import { BUILDER_TOAST_CONFIG } from "../CreateEditJourney/utils/constants";

export interface AddNodeArgs {
  node: {
    nodeType: NODE_TYPE;
    parentNodeId?: string;
    nodeData?: WorkflowNodeData;
  };
  edgeId?: string;
}

export interface AddBulkNodesArgs {
  nodes: {
    nodeType: NODE_TYPE;
    parentNodeId?: string;
    nodeData?: WorkflowNodeData;
  }[];
}

export interface PasteCopiedNodeArgs {
  /** Node the pasted copy attaches under. */
  parentNodeId: string;
  /** Splice target — the existing child to paste in front of (edge paste only). */
  childNodeId?: string;
  /** Edge to splice into locally (edge paste only). */
  edgeId?: string;
}

export interface UseFlowGraphHookReturn {
  /**
   * Create a new node in the graph.
   * @param args.node - The node configuration
   * @param args.node.nodeType - The type of node to add
   * @param args.node.parentNodeId - The id of the parent node to add the node to. If not provided, the node will be added as a root node.
   * @param args.node.nodeData - The data of the node to add. If empty, a default node data will be created.
   * @param args.edgeId - The edge id to add the node to (for intermediate insertion)
   */
  addNode: (args: AddNodeArgs) => RFNodeType;
  addBulkNodes: (args: AddBulkNodesArgs) => RFNodeType[];
  /**
   * Add a node locally and immediately persist it to the BE.
   * Used for non-Trigger nodes so the BE-assigned id is canonical from the start
   * (no local/server transformations needed). The node remains `isComplete=false`
   * until the user fills in the form and saves.
   * @returns the local node id (caller may need it for warning/recovery flows)
   */
  addNodeAndPersist: (args: AddNodeArgs) => Promise<RFNodeType>;
  /**
   * Paste the copied node (workflowUIState.copiedNodeId) under `parentNodeId`,
   * or into an edge when `childNodeId`/`edgeId` are given. Server-first: the BE
   * clones the node and returns the copy, so nothing is inserted locally until
   * the request succeeds (no rollback needed).
   */
  pasteCopiedNode: (args: PasteCopiedNodeArgs) => Promise<RFNodeType | null>;
  removeNode: (nodeId: string) => void;
  isPersistingNode: boolean;
  isPastingNode: boolean;
}

export const useFlowGraphHook = (): UseFlowGraphHookReturn => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const [addNodeMutation, { isLoading: isPersistingNode }] =
    useAddNodeToJourneyMutation();
  const [pasteNodeMutation, { isLoading: isPastingNode }] =
    usePasteNodeMutation();

  const addNode = useCallback(
    ({ node, edgeId }: AddNodeArgs): RFNodeType => {
      const { nodesById, nodeIds, edgesById, edgeIds } =
        store.getState().dynamicTrigger.workflowGraphState;
      const graph = { nodesById, nodeIds, edgesById, edgeIds };

      let newRFNode: RFNodeType;
      if (node.nodeData) {
        newRFNode = createRFNode(node.nodeType, false, node.nodeData);
      } else {
        const prevAvailableFields = node?.parentNodeId
          ? graph.nodesById[node.parentNodeId]?.data.availableFields
          : [];
        const prevRelativeTimeFields = node?.parentNodeId
          ? graph.nodesById[node.parentNodeId]?.data.relativeTimeFields
          : [];
        const journeyId =
          store.getState().dynamicTrigger.journeyDetails.journeyId;
        const defaultNodeData = getDefaultNodeData(
          node.nodeType,
          journeyId,
          node.parentNodeId ? { parentNodeId: node.parentNodeId } : undefined,
          prevAvailableFields,
          prevRelativeTimeFields,
        );
        newRFNode = createRFNode(node.nodeType, true, defaultNodeData);
      }

      const result = insertNode(newRFNode, graph, edgeId);
      const insertedNode = result.nodesById[newRFNode.id] ?? newRFNode;
      const { nodes, edges } = denormalizeGraph(result);
      dispatch(setNodesAndEdges({ nodes, edges }));
      dispatch(setPlacementIntent(null));
      return insertedNode;
    },
    [dispatch, store],
  );

  const addBulkNodes = useCallback(
    ({ nodes: nodesToAdd }: AddBulkNodesArgs): RFNodeType[] => {
      const { nodesById, nodeIds, edgesById, edgeIds } =
        store.getState().dynamicTrigger.workflowGraphState;
      let currentGraph = { nodesById, nodeIds, edgesById, edgeIds };
      const addedNodes: RFNodeType[] = [];

      for (const node of nodesToAdd) {
        let newNode: RFNodeType;
        if (node.nodeData) {
          newNode = createRFNode(node.nodeType, false, node.nodeData);
        } else {
          const prevAvailableFields = node.parentNodeId
            ? currentGraph.nodesById[node.parentNodeId]?.data.availableFields
            : [];
          const prevRelativeTimeFields = node.parentNodeId
            ? currentGraph.nodesById[node.parentNodeId]?.data.relativeTimeFields
            : [];
          const journeyId =
            store.getState().dynamicTrigger.journeyDetails.journeyId;
          const defaultNodeData = getDefaultNodeData(
            node.nodeType,
            journeyId,
            node.parentNodeId ? { parentNodeId: node.parentNodeId } : undefined,
            prevAvailableFields,
            prevRelativeTimeFields,
          );
          newNode = createRFNode(node.nodeType, true, defaultNodeData);
        }
        currentGraph = insertNode(newNode, currentGraph);
        addedNodes.push(newNode);
      }

      const { nodes, edges } = denormalizeGraph(currentGraph);
      dispatch(setNodesAndEdges({ nodes, edges }));
      dispatch(setPlacementIntent(null));
      return addedNodes;
    },
    [dispatch, store],
  );

  const removeNode = useCallback(
    (nodeId: string) => {
      dispatch(removeNodeFromGraph(nodeId));
    },
    [dispatch],
  );

  const addNodeAndPersist = useCallback(
    async (args: AddNodeArgs): Promise<RFNodeType> => {
      if (args.node.nodeType === TRIGGER_NODE_TYPES.TRIGGER_EVENT_HANDLER) {
        throw new Error(
          "Trigger nodes are not BE-persisted on placement; use addNode instead.",
        );
      }

      const localNode = addNode(args);
      const journeyId =
        store.getState().dynamicTrigger.journeyDetails.journeyId;

      const payload: AddNodeToJourneyPayload = {
        journeyId,
        nodeType: localNode.data.nodeType,
        nodeCategory: NODE_TYPE_TO_CATEGORY[localNode.data.nodeType],
        parentNodeId: localNode.data.parentNodeId ?? "",
        formValues: {},
        ...(localNode.data.nodeType === RULE_NODE_TYPES.DELAY ? { nodeName: "Wait Time Added" } : {nodeName: localNode.data.nodeName}),
        ...(localNode.data.childNodeIds &&
        localNode.data.childNodeIds.length > 0
          ? { childNodeId: localNode.data.childNodeIds[0] }
          : {}),
      };

      try {
        const res = (await addNodeMutation(
          payload,
        ).unwrap()) as NodeMutationResponse;
        dispatch(
          setNodeFromApiByPreviousId({
            previousId: localNode.id,
            apiNodeData: res.node,
            markAsSaved: true,
            markAsConfigured: false,
          }),
        );

        if (res.invalidNodes && res.invalidNodes.length > 0) {
          dispatch(
            markNodesInvalid(res.invalidNodes.map((node) => node.nodeId)),
          );
        }

        const updatedNode =
          store.getState().dynamicTrigger.workflowGraphState.nodesById[
            res.node.id
          ];
        return updatedNode ?? localNode;
      } catch (error) {
        dispatch(removeNodeFromGraph(localNode.id));
        throw error;
      }
    },
    [addNode, addNodeMutation, dispatch, store],
  );

  const pasteCopiedNode = useCallback(
    async ({
      parentNodeId,
      childNodeId,
      edgeId,
    }: PasteCopiedNodeArgs): Promise<RFNodeType | null> => {
      const { copiedNodeId } = store.getState().dynamicTrigger.workflowUIState;
      if (!copiedNodeId) return null;

      const payload: PasteNodePayload = {
        nodeId: copiedNodeId,
        parentNodeId,
        ...(childNodeId ? { childNodeId } : {}),
      };
      const res = (await pasteNodeMutation(
        payload,
      ).unwrap()) as PasteNodeResponse;

      if (res.childNodes && res.childNodes.length > 0) {
        // Split copy: insert the split first with childNodeIds cleared so each
        // branch insertion re-links it (avoids duplicate child references).
        addBulkNodes({
          nodes: [{ ...res.node, childNodeIds: [] }, ...res.childNodes].map(
            (node) => ({ nodeType: node.nodeType, nodeData: node }),
          ),
        });
      } else {
        addNode({
          node: { nodeType: res.node.nodeType, nodeData: res.node },
          edgeId,
        });
      }

      if (res.invalidNodes && res.invalidNodes.length > 0) {
        dispatch(markNodesInvalid(res.invalidNodes.map((node) => node.nodeId)));
      }

      // Clipboard is single-use: clear it after a successful paste so the
      // "Paste node" option/highlight goes away until the user copies again.
      dispatch(setCopiedNode(null));
      dispatch(setPendingPanTarget({ nodeId: res.node.id }));
      customToast(BUILDER_TOAST_CONFIG.NODE_PASTED, "success");

      // Pasting a field-affecting node or a split changes downstream
      // availableFields — refetch journey details to resync (same as add flow).
      if (
        FIELD_AFFECTING_NODE_TYPES.has(res.node.nodeType) ||
        (res.childNodes && res.childNodes.length > 0)
      ) {
        const journeyId =
          store.getState().dynamicTrigger.journeyDetails.journeyId;
        dispatch(
          dynamicTriggerEngineApi.util.invalidateTags([
            { type: "JourneyDetails", id: journeyId },
          ]),
        );
      }

      return (
        store.getState().dynamicTrigger.workflowGraphState.nodesById[
          res.node.id
        ] ?? null
      );
    },
    [addNode, addBulkNodes, dispatch, pasteNodeMutation, store],
  );

  return {
    addNode,
    addBulkNodes,
    addNodeAndPersist,
    pasteCopiedNode,
    removeNode,
    isPersistingNode,
    isPastingNode,
  };
};
